import { FormControlLabel, Link, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';

import { useDevice } from '@/api/devices';
import { usePostActivateSharing, usePostDeactivateSharing } from '@/api/shareData';
import { ActionDialog } from '@/components/ActionDialog';
import { CopyButton } from '@/components/CopyButton';
import { Switch } from '@/components/ui/Switch';
import { useSpotterContext } from '@/context/SpotterContext';
import { useDeviceIdentity } from '@/hooks/useDeviceIdentity';
import { useDisclosure } from '@/hooks/useDisclosure';

export const ShareDataToggleSection = ({ readOnly }: { readOnly?: boolean }) => {
    const { isOpen: isConfirmDialogOpen, open: openConfirmDialog, close: closeConfirmDialog } = useDisclosure();
    const { spotterId } = useSpotterContext();
    const { mutateAsync: activateDataSharing } = usePostActivateSharing();
    const { mutateAsync: deactivateDataSharing } = usePostDeactivateSharing();
    const { data: device, isPending: isDevicesPending } = useDevice();
    const deviceId = useDeviceIdentity();

    const {
        isOpen: isShareDataSwitchOn,
        toggle: toggleShareDataSwitch,
        open: switchShareDataOn,
        close: switchShareDataOff,
    } = useDisclosure(device?.sharingPermissions.public);

    const handleSwitchClick = async () => {
        if (!spotterId) {
            // eslint-disable-next-line no-console
            console.log('No Spotter selected');
            return;
        }
        if (isShareDataSwitchOn) {
            await deactivateDataSharing({ spotterId: spotterId });
            toggleShareDataSwitch();
            return;
        }
        openConfirmDialog();
    };

    const onDataShareConfirm = async () => {
        if (!spotterId) {
            // eslint-disable-next-line no-console
            console.log('No Spotter selected');
            return;
        }
        await activateDataSharing({ spotterId: spotterId });
        closeConfirmDialog();
        toggleShareDataSwitch();
    };

    const onDataSharingReject = () => {
        closeConfirmDialog();
    };

    useEffect(() => {
        if (!device) {
            return;
        }
        const isDataShareOn = device.sharingPermissions.public;
        if (isShareDataSwitchOn === isDataShareOn) {
            return;
        }

        if (isDataShareOn) {
            switchShareDataOn();
        } else {
            switchShareDataOff();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device]);

    if (!isDevicesPending && !device?.sharingPermissions.allowed) {
        return null;
    }

    return (
        <Stack gap={2}>
            <Typography variant="h4">Data sharing</Typography>
            <FormControlLabel
                value="start"
                sx={{
                    ml: 0,
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: 5,
                }}
                control={<Switch onClick={handleSwitchClick} checked={isShareDataSwitchOn} readOnly={readOnly} />}
                label={
                    <Stack>
                        <Typography>Share data with the community</Typography>
                        <Typography variant="minorLabel">
                            Share your Spotter&apos;s location and observation data with other users and the general
                            public.
                        </Typography>
                    </Stack>
                }
                labelPlacement="start"
            />
            {device?.sharingPermissions.public && (
                <Stack sx={{ gap: 2 }}>
                    <Stack>
                        <Typography variant="subtitle1">Public link to this Spotter:</Typography>
                        <Typography variant="subtitle1">
                            <ShareLink url={`${window.location.origin}/public/${spotterId}`} />
                        </Typography>
                    </Stack>
                    {deviceId?.ownerId && (
                        <Stack>
                            <Typography variant="subtitle1">Public link to all shared Spotters:</Typography>
                            <Typography variant="subtitle1">
                                <ShareLink url={`${window.location.origin}/public?user=${deviceId?.ownerId}`} />
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}
            <ActionDialog
                isOpen={isConfirmDialogOpen}
                onReject={onDataSharingReject}
                onConfirm={onDataShareConfirm}
                title="Data Sharing Confirmation"
                message={[
                    `Are you sure you want to share Spotter ${spotterId} data with the community?`,
                    `The data will become public.`,
                ]}
            />
        </Stack>
    );
};

const ShareLink = ({ url }: { url: string }) => (
    <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Link href={url} target="_blank">
            {url}
        </Link>
        <CopyButton text={url} />
    </Stack>
);
